import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../base/icons/components/Icon';
import { IconCloseLarge, IconSidePanelPopout } from '../../../base/icons/svg';
import { getUnreadCount, getUnreadFilesCount } from '../../../chat/functions';
import { getUnreadPollCount } from '../../../polls/functions';
import { closeAllSidePanels, closeSidePanelTab, popOutSidePanelTab, setActiveSidePanelTab } from '../../actions';
import { SidePanelTabId } from '../../constants';
import { SIDE_PANEL_TAB_META } from '../../tabMeta';
import { createDefaultFloatingGeometry, getSidePanelState, isSidePanelPopoutEnabled, shouldUseSidePanelBottomDrawer } from '../../functions';
import logger from '../../logger';
const useStyles = makeStyles()((theme, { useBottomDrawer }) => {
    return {
        bar: {
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.ui04}`,
            boxSizing: 'border-box',
            display: 'flex',
            flexShrink: 0,
            gap: theme.spacing(1),
            height: '48px',
            justifyContent: useBottomDrawer ? 'space-between' : undefined,
            padding: `0 ${theme.spacing(2)}`
        },
        drawerTitle: {
            ...theme.typography.heading6,
            color: theme.palette.text01,
            flex: 1,
            margin: 0,
            overflow: 'hidden',
            textAlign: 'center',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        tabs: {
            alignItems: 'stretch',
            display: useBottomDrawer ? 'none' : 'flex',
            flex: 1,
            gap: '2px',
            minWidth: 0,
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
                display: 'none'
            }
        },
        tab: {
            ...theme.typography.labelBold,
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            borderRadius: '8px 8px 0 0',
            color: theme.palette.text02,
            cursor: 'pointer',
            display: 'flex',
            flexShrink: 0,
            gap: '6px',
            maxWidth: '140px',
            padding: '8px 10px',
            '&:hover': {
                backgroundColor: theme.palette.ui02,
                color: theme.palette.text01
            },
            '&.selected': {
                backgroundColor: theme.palette.ui02,
                color: theme.palette.text01
            }
        },
        tabLabel: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        tabClose: {
            alignItems: 'center',
            borderRadius: '4px',
            display: 'flex',
            flexShrink: 0,
            height: '18px',
            justifyContent: 'center',
            opacity: 0.7,
            width: '18px',
            '&:hover': {
                backgroundColor: theme.palette.ui03,
                opacity: 1
            }
        },
        badge: {
            ...theme.typography.labelBold,
            backgroundColor: theme.palette.action01,
            borderRadius: '8px',
            color: theme.palette.text01,
            fontSize: '10px',
            lineHeight: '14px',
            minWidth: '14px',
            padding: '0 4px',
            textAlign: 'center'
        },
        actions: {
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0,
            gap: theme.spacing(1)
        },
        actionButton: {
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            borderRadius: '6px',
            color: theme.palette.icon01,
            cursor: 'pointer',
            display: 'flex',
            height: '28px',
            justifyContent: 'center',
            padding: 0,
            width: '28px',
            '&:hover': {
                backgroundColor: theme.palette.ui02
            }
        }
    };
});
const TAB_META = SIDE_PANEL_TAB_META;
/**
 * Browser-like tab bar for docked side panel apps.
 * 手机端底部抽屉仅展示当前应用标题与关闭按钮，不展示多页签栏。
 *
 * @returns {JSX.Element}
 */
export default function SidePanelTabBar() {
    const useBottomDrawer = shouldUseSidePanelBottomDrawer();
    const { classes, cx } = useStyles({ useBottomDrawer });
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openTabs, activeTab } = useSelector(getSidePanelState);
    const popoutEnabled = useSelector(isSidePanelPopoutEnabled);
    const unreadMessages = useSelector((state) => getUnreadCount(state));
    const unreadPolls = useSelector((state) => getUnreadPollCount(state));
    const unreadFiles = useSelector((state) => getUnreadFilesCount(state));
    const state = useSelector((s) => s);
    const activeMeta = activeTab ? TAB_META[activeTab] : undefined;
    const onSelect = useCallback((tabId) => {
        dispatch(setActiveSidePanelTab(tabId));
    }, [dispatch]);
    const onCloseTab = useCallback((event, tabId) => {
        event.stopPropagation();
        logger.info('关闭侧栏页签', { tabId });
        dispatch(closeSidePanelTab(tabId));
    }, [dispatch]);
    const onPopOut = useCallback(() => {
        if (!activeTab || !popoutEnabled) {
            return;
        }
        const geometry = createDefaultFloatingGeometry(state);
        logger.info('弹出侧栏页签为浮窗', { tabId: activeTab });
        dispatch(popOutSidePanelTab(activeTab, geometry));
    }, [activeTab, dispatch, popoutEnabled, state]);
    const onCloseAll = useCallback(() => {
        logger.info(useBottomDrawer ? '关闭手机端底部抽屉' : '关闭所有侧栏应用');
        dispatch(closeAllSidePanels());
    }, [dispatch, useBottomDrawer]);
    if (useBottomDrawer) {
        return (React.createElement("div", { className: classes.bar },
            React.createElement("div", { "aria-hidden": true, className: classes.actionButton }),
            React.createElement("h2", { className: classes.drawerTitle }, activeMeta ? t(activeMeta.labelKey) : ''),
            React.createElement("div", { className: classes.actions },
                React.createElement("button", { "aria-label": t('sidePanel.closeAll'), className: classes.actionButton, onClick: onCloseAll, title: t('sidePanel.closeAll'), type: 'button' },
                    React.createElement(Icon, { size: 18, src: IconCloseLarge })))));
    }
    return (React.createElement("div", { className: classes.bar },
        React.createElement("div", { className: classes.tabs }, openTabs.map(tabId => {
            const meta = TAB_META[tabId];
            const selected = activeTab === tabId;
            const unreadForTab = tabId === SidePanelTabId.CHAT
                ? unreadMessages
                : tabId === SidePanelTabId.POLLS
                    ? unreadPolls
                    : tabId === SidePanelTabId.FILE_SHARING
                        ? unreadFiles
                        : 0;
            const showBadge = !selected && unreadForTab > 0;
            return (React.createElement("button", { "aria-selected": selected, className: cx(classes.tab, selected && 'selected'), key: tabId, onClick: () => onSelect(tabId), role: 'tab', type: 'button' },
                React.createElement(Icon, { size: 16, src: meta.icon }),
                React.createElement("span", { className: classes.tabLabel }, t(meta.labelKey)),
                showBadge && (React.createElement("span", { className: classes.badge }, unreadForTab)),
                React.createElement("span", { "aria-label": t('sidePanel.closeTab'), className: classes.tabClose, onClick: event => onCloseTab(event, tabId), role: 'button' },
                    React.createElement(Icon, { size: 12, src: IconCloseLarge }))));
        })),
        React.createElement("div", { className: classes.actions },
            popoutEnabled && activeTab && (React.createElement("button", { "aria-label": t('sidePanel.popOut'), className: classes.actionButton, onClick: onPopOut, title: t('sidePanel.popOut'), type: 'button' },
                React.createElement(Icon, { size: 18, src: IconSidePanelPopout }))),
            React.createElement("button", { "aria-label": t('sidePanel.closeAll'), className: classes.actionButton, onClick: onCloseAll, title: t('sidePanel.closeAll'), type: 'button' },
                React.createElement(Icon, { size: 18, src: IconCloseLarge })))));
}
